import { createFileRoute, Link } from '@tanstack/react-router';

export const Route = createFileRoute('/services/first-home-buyer')({
  component: FirstHomeBuyerPage,
  head: () => ({
    meta: [
      { title: 'First home buyer guide — Warren-Blackwood Electorate Guide' },
      {
        name: 'description',
        content:
          'Plain-English WA first home buyer guide: First Home Owner Grant, stamp duty concessions, Keystart low-deposit loans and the federal Home Guarantee Scheme — with dollar figures and links.',
      },
      { property: 'og:title', content: 'First home buyer guide — Warren-Blackwood' },
      {
        property: 'og:description',
        content:
          'FHOG, stamp duty concessions, Keystart and the federal Home Guarantee Scheme explained in plain English, with current thresholds.',
      },
    ],
  }),
});

function FirstHomeBuyerPage() {
  const h2: React.CSSProperties = {
    fontFamily: 'var(--font-heading)',
    fontSize: '1.5rem',
    color: 'var(--rivergum)',
    marginTop: 'var(--sp-xl)',
    marginBottom: 'var(--sp-sm)',
    borderBottom: '1px solid var(--light-border)',
    paddingBottom: '0.3rem',
    letterSpacing: '0.02em',
  };
  const h3: React.CSSProperties = {
    fontFamily: 'var(--font-heading)',
    fontSize: '1.1rem',
    color: 'var(--ink)',
    marginTop: 'var(--sp-md)',
    marginBottom: '0.4rem',
    letterSpacing: '0.02em',
  };
  const p: React.CSSProperties = {
    color: 'var(--ink)',
    lineHeight: 1.65,
    marginBottom: 'var(--sp-sm)',
  };
  const link: React.CSSProperties = { color: 'var(--green-mid)', fontWeight: 600 };
  const callout = (bg: string, border: string): React.CSSProperties => ({
    background: bg,
    border: `1px solid ${border}`,
    borderRadius: 8,
    padding: 'var(--sp-md)',
    marginBottom: 'var(--sp-md)',
  });

  return (
    <main
      id="main-content"
      style={{
        maxWidth: 760,
        margin: '0 auto',
        padding: 'var(--sp-lg) var(--sp-md) var(--sp-xl)',
      }}
    >
      <p
        style={{
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          fontSize: '0.75rem',
          fontWeight: 700,
          color: 'var(--green-mid)',
          margin: 0,
          marginBottom: '0.35rem',
        }}
      >
        Services · First home buyers
      </p>
      <h1
        style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(2rem, 5vw, 2.75rem)',
          color: 'var(--green)',
          margin: 0,
          marginBottom: 'var(--sp-md)',
          lineHeight: 1.1,
        }}
      >
        First home buyer guide
      </h1>
      <p
        style={{
          color: 'var(--ink-mid)',
          fontStyle: 'italic',
          fontSize: '1.05rem',
          marginBottom: 'var(--sp-lg)',
          lineHeight: 1.5,
        }}
      >
        WA and federal schemes can take tens of thousands off the cost of your
        first home — but many regional first home buyers never apply because
        they don't know they exist. Here's the plain-English summary, with
        current dollar figures.
      </p>

      <div style={callout('#eef5fb', '#b9d4e8')}>
        <strong style={{ color: '#1b4f72' }}>The four schemes in one line</strong>
        <ol style={{ ...p, margin: '0.4rem 0 0', paddingLeft: '1.4rem', fontSize: '0.95rem' }}>
          <li><strong>First Home Owner Grant</strong> — $10,000 if you build or buy brand new.</li>
          <li><strong>Stamp duty concession</strong> — pay no duty up to $500,000; reduced duty to $750,000 outside Perth and Peel (including Warren-Blackwood).</li>
          <li><strong>Keystart</strong> — WA low-deposit home loan (from 2%), no LMI, income-tested.</li>
          <li><strong>Home Guarantee Scheme</strong> — federal scheme: buy with a 5% deposit, no Lender's Mortgage Insurance.</li>
        </ol>
      </div>

      <h2 style={h2}>1. WA First Home Owner Grant (FHOG)</h2>
      <p style={p}>
        A one-off <strong>$10,000 grant</strong> from the WA Government for
        first home buyers who <strong>build a new home or buy a brand-new
        one</strong> (off the plan, spec build, or substantially renovated).
        Established homes <em>don't</em> qualify for FHOG — they qualify for
        the stamp duty concession below instead.
      </p>
      <h3 style={h3}>Who can get it</h3>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>You're 18+, an Australian citizen or permanent resident.</li>
        <li>Neither you nor your spouse/de facto has previously owned a home in Australia.</li>
        <li>
          You'll live in the home as your principal place of residence for at
          least <strong>6 continuous months within 12 months</strong> of
          settlement or completion.
        </li>
        <li>
          The property value cap is <strong>$750,000 south of the 26th
          parallel</strong> (Warren-Blackwood sits in this zone) and
          $1,000,000 north of it.
        </li>
      </ul>
      <p style={p}>
        Apply through your lender at settlement, or directly to RevenueWA if
        you're owner-building. <a href="https://www.wa.gov.au/service/financial-management/financial-grants-and-subsidies/first-home-owner-grant" style={link}>WA First Home Owner Grant — official page →</a>
      </p>

      <h2 style={h2}>2. Stamp duty (transfer duty) concession</h2>
      <p style={p}>
        WA's <strong>First Home Owner Rate of Duty</strong> can save first
        home buyers thousands in transfer duty. The thresholds were raised in
        March 2025.{' '}
        <a href="https://www.wa.gov.au/organisation/department-of-finance/transfer-duty-assessment" style={link}>
          Use the RevenueWA duty calculator to see your saving.
        </a>
      </p>
      <h3 style={h3}>Established home or new home you're buying</h3>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li><strong>$0 to $500,000</strong> — no transfer duty at all.</li>
        <li><strong>$500,001 to $750,000</strong> — reduced duty on a sliding scale.</li>
        <li><strong>Above $750,000</strong> — standard transfer duty applies (no concession).</li>
      </ul>
      <p style={{ ...p, fontSize: '0.92rem', color: 'var(--ink-mid)' }}>
        These are the regional thresholds (outside Perth and Peel). In Perth
        and Peel the upper limit is $700,000.
      </p>
      <h3 style={h3}>Vacant land you're buying to build on</h3>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li><strong>$0 to $350,000</strong> — no transfer duty.</li>
        <li><strong>$350,001 to $450,000</strong> — reduced duty.</li>
        <li><strong>Above $450,000</strong> — standard duty.</li>
      </ul>
      <p style={p}>
        You apply via the duty form your settlement agent or conveyancer
        lodges at settlement — there's no separate application to lodge in
        advance.{' '}
        <a href="https://www.wa.gov.au/organisation/department-of-finance/first-home-owner-rate-of-duty" style={link}>
          First Home Owner Rate of Duty — RevenueWA →
        </a>
      </p>
      <div style={callout('#fff7e6', '#f0c674')}>
        <strong style={{ color: '#7a5300' }}>You can stack them</strong>
        <p style={{ ...p, margin: '0.4rem 0 0', fontSize: '0.92rem' }}>
          If you're <em>building</em> a new home in Warren-Blackwood on land
          under $350,000 and your finished build comes in under $750,000, you
          may qualify for the $10,000 grant <em>and</em> pay no transfer duty
          on the land. Confirm your specific numbers with your lender or
          settlement agent.
        </p>
      </div>

      <h2 style={h2}>3. Keystart — WA low-deposit home loans</h2>
      <p style={p}>
        <strong>Keystart</strong> is the WA Government's home loan provider for
        people who can afford repayments but can't save a big deposit.
        Keystart loans don't require Lender's Mortgage Insurance (LMI), and
        deposits can be <strong>as low as 2%</strong> of the purchase price
        for the standard product.
      </p>
      <h3 style={h3}>Keystart in plain English</h3>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>No LMI, no monthly account-keeping fees.</li>
        <li>Income limits apply — checked at application, then never again.</li>
        <li>
          Income and property price caps are <strong>higher in regional WA
          (including Warren-Blackwood)</strong> than in metro Perth.
        </li>
        <li>
          Once your principal balance drops below 80% of the property's
          value, Keystart expects you to refinance to a mainstream lender —
          the program is a starter, not a forever loan.
        </li>
      </ul>
      <p style={p}>
        Income and property price caps change every financial year. Check
        the current Warren-Blackwood thresholds directly:{' '}
        <a href="https://www.keystart.com.au/home-loans" style={link}>keystart.com.au/home-loans →</a>{' '}
        or phone Keystart on{' '}
        <a href="tel:1300578278" style={link}>1300 578 278</a>.
      </p>
      <p style={p}>
        <em>Note: HomeStart is South Australia's equivalent scheme and isn't
        available to WA buyers — Keystart is the WA program.</em>
      </p>

      <h2 style={h2}>4. Federal Home Guarantee Scheme</h2>
      <p style={p}>
        The <strong>Home Guarantee Scheme</strong> is a federal program run
        through Housing Australia and a panel of participating lenders. The
        government guarantees part of your loan so you can buy with a smaller
        deposit and avoid LMI.
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          <strong>First Home Guarantee</strong> — now called the 5% Deposit
          Scheme. Buy your first home with a 5% deposit, no LMI. Since 1
          October 2025 there is no income test and no cap on places, and the
          property price caps were raised.
        </li>
        <li>
          <strong>Family Home Guarantee</strong> — single parents and single
          legal guardians can buy with a 2% deposit, no LMI.
        </li>
        <li>
          <strong>Regional First Home Buyer Guarantee</strong> — was merged
          into the standard scheme in 2025; regional buyers no longer need
          the separate guarantee.
        </li>
      </ul>
      <p style={p}>
        You apply through a participating lender, not directly to the
        government. <a href="https://www.housingaustralia.gov.au/support-buy-home" style={link}>Home Guarantee Scheme — Housing Australia →</a>
      </p>

      <h2 style={h2}>Quick worked example</h2>
      <div style={callout('#f3efe6', 'var(--rule)')}>
        <p style={{ ...p, margin: 0, fontSize: '0.95rem' }}>
          <strong>Sam &amp; Jess buy an established 3-bed in Manjimup for $450,000.</strong>
        </p>
        <ul style={{ ...p, paddingLeft: '1.2rem', margin: '0.5rem 0 0', fontSize: '0.92rem' }}>
          <li>FHOG: not eligible (established home) — <strong>$0</strong>.</li>
          <li>Transfer duty: under the $500,000 threshold — <strong>$0 duty</strong>.</li>
          <li>Keystart loan with a 2% deposit: <strong>$9,000 cash needed</strong> for deposit + buffer for fees.</li>
          <li>Total upfront, excluding conveyancing and inspection fees: <strong>~$11,000–$13,000</strong>.</li>
        </ul>
        <p style={{ ...p, margin: '0.5rem 0 0', fontSize: '0.85rem', color: 'var(--ink-mid)' }}>
          Illustrative only. Run your own numbers with a lender, broker or
          financial counsellor before committing.
        </p>
      </div>

      <h2 style={h2}>Free advice before you sign anything</h2>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          <strong>National Debt Helpline</strong> — <a href="tel:1800007007" style={link}>1800 007 007</a> — free
          financial counsellors, including pre-purchase budget reality checks.
        </li>
        <li>
          <strong>Consumer Protection WA</strong> — <a href="tel:1300304054" style={link}>1300 304 054</a> — for
          questions about contracts, settlement agents and builders.
        </li>
        <li>
          <strong>MoneySmart</strong> — <a href="https://moneysmart.gov.au/buying-a-home" style={link}>moneysmart.gov.au/buying-a-home</a> —
          ASIC's calculators and plain-English guides.
        </li>
      </ul>

      <h2 style={h2}>When the MP's office can help</h2>
      <p style={p}>
        First home buying is mostly process — lenders, conveyancers, RevenueWA,
        Keystart and Housing Australia handle the day-to-day. The electorate
        office can help if you're stuck on the system itself: a delayed FHOG
        decision, a Keystart application that's gone sideways, or a
        state-government concession you've been told you don't qualify for
        but you think you do. See the{' '}
        <Link to="/contact" style={link}>contact page</Link> for office hours.
      </p>

      <h2 style={h2}>Trusted sources</h2>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          <a href="https://www.wa.gov.au/service/financial-management/financial-grants-and-subsidies/first-home-owner-grant" style={link}>
            wa.gov.au — First Home Owner Grant
          </a>
        </li>
        <li>
          <a href="https://www.wa.gov.au/organisation/department-of-finance/first-home-owner-rate-of-duty" style={link}>
            wa.gov.au — First Home Owner Rate of Duty
          </a>
        </li>
        <li>
          <a href="https://www.keystart.com.au/" style={link}>keystart.com.au</a> — Keystart Home Loans
        </li>
        <li>
          <a href="https://www.housingaustralia.gov.au/support-buy-home" style={link}>
            housingaustralia.gov.au — Home Guarantee Scheme
          </a>
        </li>
        <li>
          <a href="https://moneysmart.gov.au/buying-a-home" style={link}>moneysmart.gov.au — Buying a home</a>
        </li>
      </ul>

      <p
        style={{
          marginTop: 'var(--sp-lg)',
          fontSize: '0.78rem',
          color: 'var(--ink-light)',
          borderTop: '1px solid var(--rule)',
          paddingTop: 'var(--sp-sm)',
          lineHeight: 1.5,
        }}
      >
        Community summary of publicly-available information from RevenueWA,
        Keystart and Housing Australia. Thresholds and dollar figures change
        with each WA and Commonwealth budget — confirm current numbers on the
        official sites above before acting. Spot something out of date?{' '}
        <a
          href="mailto:bevan.eatts@mp.wa.gov.au?subject=Warren-Blackwood%20guide%20%E2%80%94%20first%20home%20buyer%20update"
          style={{ color: 'var(--green-mid)', fontWeight: 500 }}
        >
          Email the office
        </a>
        . Last reviewed: June 2026.
      </p>
    </main>
  );
}
